import { ActionType as AccessTokenActionType, Action as AccessTokenAction } from '~/AccessToken';
import { Pagination } from '~/Api/Pagination';
import { ProductHistoryEvent } from '~/Api/Product/History';

import ActionTypes from './ActionTypes';

export interface EventsState {
	request: Promise<any>;
	isFetching: boolean;
	events: ProductHistoryEvent[];
	pagination: Pagination | null;
}

export interface State {
	byGroupId: { [key: string]: EventsState };
}

type Action =
	| {
			type: ActionTypes.request;
			request: Promise<any>;
			groupId: string;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			groupId: string;
			events: ProductHistoryEvent[];
			pagination: Pagination;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			groupId: string;
			events: null;
			pagination: null;
	  }
	| { type: ActionTypes.prune; groupId: string };

function createState(): State {
	return {
		byGroupId: {},
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionTypes.request: {
			const eventsState = state.byGroupId[action.groupId];

			return {
				...state,
				byGroupId: {
					...state.byGroupId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						events: eventsState?.events || [],
						pagination: eventsState?.pagination || null,
					},
				},
			};
		}

		case ActionTypes.receive: {
			const eventsState = state.byGroupId[action.groupId];
			if (eventsState?.request !== action.request) {
				return state;
			}

			if (!action.events) {
				const byGroupId = { ...state.byGroupId };
				delete byGroupId[action.groupId];
				return { ...state, byGroupId };
			}

			const { events, pagination } = action;
			const newEvents = [...eventsState.events];
			newEvents.splice(pagination.getFirstIndex(), pagination.perPage, ...events);

			return {
				...state,
				byGroupId: {
					...state.byGroupId,
					[action.groupId]: {
						...eventsState,
						isFetching: false,
						events: newEvents,
						pagination,
					},
				},
			};
		}

		case ActionTypes.prune: {
			const byGroupId = { ...state.byGroupId };
			delete byGroupId[action.groupId];
			return { ...state, byGroupId };
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectEventsByGroupId(state: State, groupId: string): EventsState | null {
	return state.byGroupId[groupId];
}
